import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { odooApiFetch } from "app/utils/auth";
import {
  getApiErrorMessage,
  isAccessDeniedError,
  isNotFoundError,
} from "app/utils/apiErrors";
import {
  OPENSTREETMAP_TILE_CONFIG,
  getLeafletTileLayerOptions,
} from "app/utils/mapTileConfig";
import { loadDashboardMapTileConfig } from "app/utils/routeMapConfig";
import {
  RouteMapDataPayload,
  getCachedRouteMapData,
  setCachedRouteMapData,
} from "app/utils/routeMapDataCache";
import {
  LatLngTuple,
  MapViewportMap,
  applyRouteMapViewport,
  collectRouteMapPoints,
  extendBoundsWithPoints,
  normalizePolylinePoints,
  safeRemoveMap,
  simplifyPolylinePoints,
  toLatLngTuple,
} from "app/utils/routeMapGeo";
import {
  ROUTE_MAP_PIN_ICON_ANCHOR,
  ROUTE_MAP_PIN_ICON_SIZE,
  ROUTE_MAP_PIN_POPUP_ANCHOR,
  createRouteMapPinHtml,
  getStopPinKind,
} from "app/utils/routeMapPinMarker";
import { DriverMapFocus } from "app/types/driver";
import {
  MAP_PIN_TOOLTIP_OPTIONS,
  buildMapPinTooltipHtml,
} from "app/utils/mapPinTooltip";

type LeafletControl = {
  addTo: (map: LeafletMap) => unknown;
};

type LeafletBounds = {
  extend: (coords: LatLngTuple) => LeafletBounds;
  isValid: () => boolean;
};

type LeafletDivIcon = {
  options: Record<string, unknown>;
};

type LeafletStatic = {
  map: (element: HTMLElement, options?: Record<string, unknown>) => LeafletMap;
  control: {
    zoom: (options?: { position?: string }) => LeafletControl;
  };
  tileLayer: (
    url: string,
    options?: Record<string, unknown>
  ) => { addTo: (map: LeafletMap) => unknown };
  marker: (
    coords: LatLngTuple,
    options?: Record<string, unknown>
  ) => LeafletLayer;
  polyline: (
    coords: LatLngTuple[],
    options?: Record<string, unknown>
  ) => LeafletLayer;
  layerGroup: (layers?: LeafletLayer[]) => LeafletLayerGroup;
  latLngBounds: (corner1: LatLngTuple, corner2: LatLngTuple) => LeafletBounds;
  divIcon: (options: Record<string, unknown>) => LeafletDivIcon;
};

type LeafletMap = {
  remove: () => void;
  invalidateSize: (animate?: boolean) => void;
  fitBounds: (bounds: LeafletBounds, options?: { padding?: number[] }) => void;
  setView: (coords: LatLngTuple, zoom: number) => void;
};

type LeafletLayer = {
  addTo: (map: LeafletMap | LeafletLayerGroup) => unknown;
  bindPopup: (html: string) => unknown;
  bindTooltip?: (html: string, options?: Record<string, unknown>) => unknown;
  remove?: () => void;
  openPopup?: () => void;
};

type LeafletLayerGroup = {
  clearLayers: () => void;
  addLayer: (layer: LeafletLayer) => unknown;
  addTo: (map: LeafletMap) => unknown;
};

declare global {
  interface Window {
    L?: LeafletStatic;
  }
}

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

let leafletLoader: Promise<void> | null = null;

function getApiRoot(): string {
  return (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
}

async function loadLeafletAssets(): Promise<void> {
  if (window.L) {
    return;
  }
  if (!leafletLoader) {
    leafletLoader = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = LEAFLET_CSS;
        document.head.appendChild(link);
      }
      const script = document.createElement("script");
      script.src = LEAFLET_JS;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Could not load map library."));
      document.head.appendChild(script);
    });
  }
  await leafletLoader;
}

function createPinMarker(
  L: LeafletStatic,
  point: LatLngTuple,
  kind: Parameters<typeof createRouteMapPinHtml>[0],
  tooltipJob?: {
    pickingNumber?: string;
    partnerName?: string;
    address?: string;
  },
  sequence?: number | string
): LeafletLayer {
  const marker = L.marker(point, {
    icon: L.divIcon({
      className: "route-map-pin-leaflet-icon",
      html: createRouteMapPinHtml(kind, { sequence }),
      iconSize: ROUTE_MAP_PIN_ICON_SIZE,
      iconAnchor: ROUTE_MAP_PIN_ICON_ANCHOR,
      popupAnchor: ROUTE_MAP_PIN_POPUP_ANCHOR,
    }),
  }) as LeafletLayer;

  if (tooltipJob) {
    marker.bindTooltip?.(
      buildMapPinTooltipHtml(tooltipJob),
      MAP_PIN_TOOLTIP_OPTIONS
    );
  }

  return marker;
}

function renderRouteOverlays(
  L: LeafletStatic,
  overlayGroup: LeafletLayerGroup,
  mapData: RouteMapDataPayload
): {
  bounds: LeafletBounds | null;
  mapPoints: LatLngTuple[];
  infoMessage: string | null;
} {
  overlayGroup.clearLayers();

  let mapBounds: LeafletBounds | null = null;
  const extendBounds = (point: LatLngTuple) => {
    if (!mapBounds) {
      mapBounds = L.latLngBounds(point, point);
      return;
    }
    mapBounds.extend(point);
  };

  const stops = Array.isArray(mapData.stops) ? mapData.stops : [];
  const polylinePoints = normalizePolylinePoints(mapData.polyline);
  const displayPolylinePoints = simplifyPolylinePoints(polylinePoints);

  const depot = mapData.depot;
  const depotPoint = toLatLngTuple(depot?.lat, depot?.lng);
  if (depotPoint) {
    createPinMarker(L, depotPoint, "depot", {
      pickingNumber: "Depot",
      partnerName: depot?.name || "Depot",
      address: "",
    }).addTo(overlayGroup);
    extendBounds(depotPoint);
  }

  for (const stop of stops) {
    const stopPoint = toLatLngTuple(stop.lat, stop.lng);
    if (!stopPoint) {
      continue;
    }
    const pinKind = getStopPinKind(stop.status);
    createPinMarker(
      L,
      stopPoint,
      pinKind,
      {
        pickingNumber: stop.picking_name || stop.name || "Stop",
        partnerName: stop.name,
        address: stop.address,
      },
      stop.sequence
    ).addTo(overlayGroup);
    extendBounds(stopPoint);
  }

  if (displayPolylinePoints.length > 0) {
    L.polyline(displayPolylinePoints, { color: "#dc2626", weight: 4 }).addTo(
      overlayGroup
    );
    if (!mapBounds) {
      mapBounds = L.latLngBounds(
        displayPolylinePoints[0],
        displayPolylinePoints[0]
      );
    }
    extendBoundsWithPoints(mapBounds, displayPolylinePoints);
  }

  const mapPoints = collectRouteMapPoints(mapData);
  const infoMessage =
    mapPoints.length === 0
      ? "No map data (addresses not geocoded)."
      : null;

  return { bounds: mapBounds, mapPoints, infoMessage };
}

function MapLegend({ showDriver = false }: { showDriver?: boolean }) {
  const items = [
    { color: "#2563eb", label: "Depot" },
    { color: "#f59e0b", label: "Pending" },
    { color: "#16a34a", label: "Delivered" },
    { color: "#dc2626", label: "Failed" },
    ...(showDriver ? [{ color: "#8b5cf6", label: "Driver" }] : []),
  ];

  return (
    <Box
      sx={{
        position: "absolute",
        left: 12,
        bottom: 12,
        zIndex: 3,
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
        px: 1.25,
        py: 0.75,
        bgcolor: "rgba(255,255,255,0.94)",
        borderRadius: "10px",
        border: "1px solid #e5e9ef",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
      }}
    >
      {items.map(({ color, label }) => (
        <Box
          key={label}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: color,
            }}
          />
          <Typography sx={{ fontSize: "0.68rem", color: "#475569", fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export type RouteMapProps = {
  routeId: number | null;
  accessToken: string;
  active?: boolean;
  minHeight?: number | string;
  driverFocus?: DriverMapFocus | null;
};

export function RouteMap({
  routeId,
  accessToken,
  active = true,
  minHeight = 280,
  driverFocus = null,
}: RouteMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const overlayGroupRef = useRef<LeafletLayerGroup | null>(null);
  const driverMarkerRef = useRef<LeafletLayer | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!active || !accessToken) {
      return;
    }

    let cancelled = false;

    const initMap = async () => {
      if (!mapContainerRef.current || mapRef.current) {
        return;
      }

      try {
        const apiRoot = getApiRoot();
        await loadLeafletAssets();
        const mapConfig = await loadDashboardMapTileConfig(accessToken, apiRoot);

        if (cancelled || !mapContainerRef.current || mapRef.current) {
          return;
        }

        const L = window.L;
        if (!L) {
          return;
        }

        const map = L.map(mapContainerRef.current, {
          zoomControl: false,
          attributionControl: true,
        });
        L.control.zoom({ position: "topright" }).addTo(map);

        const tileUrl =
          mapConfig.tile_url_template ||
          OPENSTREETMAP_TILE_CONFIG.tile_url_template!;
        L.tileLayer(tileUrl, getLeafletTileLayerOptions(mapConfig)).addTo(map);

        overlayGroupRef.current = L.layerGroup().addTo(map) as LeafletLayerGroup;
        mapRef.current = map;

        if (!cancelled) {
          setMapReady(true);
        }
      } catch (error) {
        console.error("Route map failed to initialize:", error);
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(
            getApiErrorMessage(error, "Map unavailable. Please try again.")
          );
        }
      }
    };

    void initMap();

    return () => {
      cancelled = true;
      safeRemoveMap(mapRef.current);
      mapRef.current = null;
      overlayGroupRef.current = null;
      setMapReady(false);
    };
  }, [active, accessToken]);

  useEffect(() => {
    if (!active || !accessToken || !routeId) {
      if (!routeId) {
        overlayGroupRef.current?.clearLayers();
        setStatus("idle");
        setErrorMessage(null);
        setInfoMessage(null);
      }
      return;
    }

    if (!mapReady || !mapRef.current || !overlayGroupRef.current) {
      return;
    }

    let cancelled = false;
    const routeIdForRequest = routeId;

    const updateRouteOverlays = async () => {
      setStatus("loading");
      setErrorMessage(null);
      setInfoMessage(null);

      try {
        const apiRoot = getApiRoot();
        let mapData = getCachedRouteMapData(routeIdForRequest);
        if (!mapData) {
          mapData = await odooApiFetch<RouteMapDataPayload>(
            `${apiRoot}/route/${routeIdForRequest}/map_data`,
            accessToken
          );
          setCachedRouteMapData(routeIdForRequest, mapData);
        }

        if (cancelled || routeId !== routeIdForRequest) {
          return;
        }

        const L = window.L;
        const map = mapRef.current;
        const overlayGroup = overlayGroupRef.current;
        if (!L || !map || !overlayGroup) {
          return;
        }

        const { mapPoints, infoMessage: overlayInfoMessage } = renderRouteOverlays(
          L,
          overlayGroup,
          mapData
        );

        const { infoMessage: viewportInfoMessage } = applyRouteMapViewport(
          map as MapViewportMap,
          (corner1, corner2) => L.latLngBounds(corner1, corner2),
          mapPoints
        );
        const nextInfoMessage = overlayInfoMessage ?? viewportInfoMessage;

        window.setTimeout(() => {
          if (!cancelled && mapRef.current === map) {
            map.invalidateSize(true);
          }
        }, 200);
        window.setTimeout(() => {
          if (!cancelled && mapRef.current === map) {
            map.invalidateSize(true);
          }
        }, 600);

        if (!cancelled && routeId === routeIdForRequest) {
          setInfoMessage(nextInfoMessage);
          setStatus("ready");
        }
      } catch (error) {
        console.error("Route map failed to render:", error);
        if (!cancelled && routeId === routeIdForRequest) {
          setStatus("error");
          if (isNotFoundError(error)) {
            setErrorMessage("Route not found.");
          } else if (isAccessDeniedError(error)) {
            setErrorMessage("You cannot view this route map.");
          } else {
            setErrorMessage(
              getApiErrorMessage(error, "Map unavailable. Please try again.")
            );
          }
        }
      }
    };

    void updateRouteOverlays();

    return () => {
      cancelled = true;
    };
  }, [active, accessToken, routeId, mapReady]);

  useEffect(() => {
    if (driverMarkerRef.current?.remove) {
      driverMarkerRef.current.remove();
      driverMarkerRef.current = null;
    }

    if (!driverFocus || status !== "ready" || !mapRef.current || !window.L) {
      return;
    }

    const point = toLatLngTuple(driverFocus.lat, driverFocus.lng);
    if (!point) {
      return;
    }

    const L = window.L;
    const map = mapRef.current;
    const marker = createPinMarker(
      L,
      point,
      "driver",
      {
        pickingNumber: driverFocus.name,
        partnerName: "Driver",
        address: "Current location",
      }
    ).addTo(map) as LeafletLayer;
    driverMarkerRef.current = marker;

    map.setView(point, 14);
    window.setTimeout(() => marker.openPopup?.(), 150);

    return () => {
      if (driverMarkerRef.current?.remove) {
        driverMarkerRef.current.remove();
        driverMarkerRef.current = null;
      }
    };
  }, [driverFocus, status]);

  useEffect(() => {
    if (!active || !mapRef.current || !mapContainerRef.current) {
      return;
    }

    const invalidate = () => {
      window.setTimeout(() => mapRef.current?.invalidateSize(true), 100);
    };

    invalidate();

    const observer = new ResizeObserver(invalidate);
    observer.observe(mapContainerRef.current);
    return () => observer.disconnect();
  }, [active, routeId, status]);

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minHeight,
        height: "100%",
        bgcolor: "#e5e7eb",
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #e5e9ef",
        "& .route-map-pin-leaflet-icon": {
          background: "transparent",
          border: "none",
        },
        "& .route-map-pin-tooltip": {
          background: "rgba(255,255,255,0.98)",
          border: "1px solid #e5e9ef",
          borderRadius: "10px",
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
          padding: "8px 10px",
          color: "#334155",
        },
        "& .route-map-pin-tooltip::before": {
          borderTopColor: "#e5e9ef",
        },
      }}
    >
      <Box
        ref={mapContainerRef}
        className="route-map-canvas"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      />
      {status === "ready" && <MapLegend showDriver={Boolean(driverFocus)} />}
      {status === "ready" && infoMessage && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            px: 1.5,
            py: 0.75,
            bgcolor: "rgba(255,255,255,0.94)",
            borderRadius: "10px",
            border: "1px solid #e5e9ef",
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>
            {infoMessage}
          </Typography>
        </Box>
      )}
      {status === "loading" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.72)",
          }}
        >
          <CircularProgress size={28} sx={{ color: "#5fa67a" }} />
        </Box>
      )}
      {status === "error" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            textAlign: "center",
            bgcolor: "#fff",
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            {errorMessage || "Map unavailable"}
          </Typography>
        </Box>
      )}
      {!routeId && status === "idle" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f8fafc",
          }}
        >
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Select a route to view the map
          </Typography>
        </Box>
      )}
    </Box>
  );
}
